import type { LoginResult } from '@vivport/backend/types/auth';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiErrorMessage } from '@/api/errors';
import { storeTokens } from '@/api/token-storage';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Notice } from '@/components/ui/notice';
import { useLogin, useMfaSendEmailCode, useMfaVerifyEmail } from '@/hooks/use-auth';

export function LoginPage() {
  const navigate = useNavigate();
  const loginMutation = useLogin();
  const sendCode = useMfaSendEmailCode();
  const verifyEmail = useMfaVerifyEmail();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const complete = (result: LoginResult) => {
    storeTokens(result);
    navigate('/accounts', { replace: true });
  };

  const submitCredentials = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const result = await loginMutation.mutateAsync({ email, password });
      if ('mfaRequired' in result) {
        await sendCode.mutateAsync(result.mfaToken);
        setMfaToken(result.mfaToken);
        return;
      }
      complete(result);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!mfaToken) return;
    setError(null);
    try {
      complete(await verifyEmail.mutateAsync({ code, mfaToken }));
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  const restart = () => {
    setMfaToken(null);
    setCode('');
    setError(null);
  };

  const pending = loginMutation.isPending || sendCode.isPending || verifyEmail.isPending;

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Vivport DBA</CardTitle>
        </CardHeader>
        <CardContent>
          {mfaToken ? (
            <form onSubmit={submitCode} className="space-y-4">
              <p className="text-sm text-muted-foreground">We sent a 6-digit code to {email}.</p>
              <div className="space-y-2">
                <Label htmlFor="code">Verification code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  required
                  autoFocus
                />
              </div>
              {error && <Notice tone="destructive">{error}</Notice>}
              <div className="flex items-center justify-between">
                <Button type="button" variant="ghost" onClick={restart} disabled={pending}>
                  Start over
                </Button>
                <Button type="submit" disabled={pending || code.length !== 6}>
                  Verify
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={submitCredentials} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </div>
              {error && <Notice tone="destructive">{error}</Notice>}
              <Button type="submit" className="w-full" disabled={pending}>
                Sign in
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
